import StateMachine from '../statemachine/StateMachine'
import { sharedInstance as events } from './EventCenter'

const TPV_SPRITESHEET_COLUMNS = 6
const TPV_MOVE_FRAME_RATE = 10
const TPV_DEATH_FRAME_RATE = 12
const TPV_DEFAULT_MOVE_SPEED = 4.5
const TPV_DEFAULT_TRAVEL_DISTANCE = 300
const TPV_DISAPPEAR_DELAY_MS = 1000
const TPV_FADE_OUT_DURATION_MS = 400
const GROUND_COLLISION_CATEGORY = 0x0002
const TPV_PLATFORM_LANDING_Y_OFFSET = 20

type TpvVoladorConfig = {
	startX: number
	travelDistance?: number
	moveSpeed?: number
	nonGroundLandingYOffset?: number
	initialDirection?: 1 | -1
	spritesheetKey?: string
	animationKeyPrefix?: string
}

export default class TpvVoladorController
{
	private scene: Phaser.Scene
	private sprite: Phaser.Physics.Matter.Sprite
	private stateMachine: StateMachine
	private moveSpeed: number
	private minX: number
	private maxX: number
	private facing: 1 | -1 = 1
	private isDead = false
	private landedAfterDeath = false
	private nonGroundLandingYOffset: number
	private initialDirection: 1 | -1
	private spritesheetKey: string
	private animationKeyPrefix: string

	constructor(scene: Phaser.Scene, sprite: Phaser.Physics.Matter.Sprite, config: TpvVoladorConfig)
	{
		this.scene = scene
		this.sprite = sprite

		const travelDistance = Math.max(0, config.travelDistance ?? TPV_DEFAULT_TRAVEL_DISTANCE)
		const halfTravelDistance = travelDistance * 0.5
		this.minX = config.startX - halfTravelDistance
		this.maxX = config.startX + halfTravelDistance
		this.moveSpeed = Math.max(0.1, config.moveSpeed ?? TPV_DEFAULT_MOVE_SPEED)
		this.nonGroundLandingYOffset = Math.max(0, config.nonGroundLandingYOffset ?? TPV_PLATFORM_LANDING_Y_OFFSET)
		this.initialDirection = config.initialDirection ?? (Math.random() < 0.5 ? -1 : 1)
		this.spritesheetKey = config.spritesheetKey ?? 'tpv-volador'
		this.animationKeyPrefix = config.animationKeyPrefix ?? 'tpv-volador'

		this.createAnimations()

		this.stateMachine = new StateMachine(this, 'tpv-volador')

		this.stateMachine
			.addState('move-right', {
				onEnter: this.moveRightOnEnter,
				onUpdate: this.moveRightOnUpdate
			})
			.addState('move-left', {
				onEnter: this.moveLeftOnEnter,
				onUpdate: this.moveLeftOnUpdate
			})
			.addState('dead', {
				onEnter: this.deadOnEnter
			})
			.setState(this.initialDirection > 0 ? 'move-right' : 'move-left')

		events.on('tpv-volador-stomped', this.handleStomped, this)

		this.sprite.setOnCollide((data: MatterJS.ICollisionPair) => {
			if (!this.isDead || this.landedAfterDeath)
			{
				return
			}

			const ownBody = this.sprite.body as MatterJS.BodyType
			const otherBody = (data.bodyA === ownBody ? data.bodyB : data.bodyA) as MatterJS.BodyType
			if (otherBody.isSensor)
			{
				return
			}

			// Solo fijamos el frame final al impactar con suelo/plataforma estática,
			// no al tocar al jugador u otros cuerpos dinámicos.
			if (!otherBody.isStatic)
			{
				return
			}

			this.landAfterDeath(otherBody)
		})
	}

	destroy()
	{
		events.off('tpv-volador-stomped', this.handleStomped, this)
	}

	update(dt: number)
	{
		this.stateMachine.update(dt)
	}

	private createAnimations()
	{
		const moveRightAnimationKey = this.animationKey('move-right')
		const moveLeftAnimationKey = this.animationKey('move-left')
		const deathRightAnimationKey = this.animationKey('death-right')
		const deathLeftAnimationKey = this.animationKey('death-left')

		const createRowFrames = (rowIndex: number, frameCount: number) => {
			const start = rowIndex * TPV_SPRITESHEET_COLUMNS
			return this.scene.anims.generateFrameNumbers(this.spritesheetKey, {
				start,
				end: start + frameCount - 1
			})
		}

		if (!this.scene.anims.exists(moveRightAnimationKey))
		{
			this.scene.anims.create({
				key: moveRightAnimationKey,
				frames: createRowFrames(0, 6),
				frameRate: TPV_MOVE_FRAME_RATE,
				repeat: -1
			})
		}

		if (!this.scene.anims.exists(moveLeftAnimationKey))
		{
			this.scene.anims.create({
				key: moveLeftAnimationKey,
				frames: createRowFrames(1, 6),
				frameRate: TPV_MOVE_FRAME_RATE,
				repeat: -1
			})
		}

		if (!this.scene.anims.exists(deathRightAnimationKey))
		{
			this.scene.anims.create({
				key: deathRightAnimationKey,
				frames: createRowFrames(2, 4),
				frameRate: TPV_DEATH_FRAME_RATE,
				repeat: 0
			})
		}

		if (!this.scene.anims.exists(deathLeftAnimationKey))
		{
			this.scene.anims.create({
				key: deathLeftAnimationKey,
				frames: createRowFrames(3, 4),
				frameRate: TPV_DEATH_FRAME_RATE,
				repeat: 0
			})
		}
	}

	private animationKey(suffix: 'move-right' | 'move-left' | 'death-right' | 'death-left')
	{
		return `${this.animationKeyPrefix}-${suffix}`
	}

	private moveRightOnEnter()
	{
		if (this.isDead)
		{
			return
		}

		this.facing = 1
		this.sprite.play(this.animationKey('move-right'), true)
	}

	private moveRightOnUpdate()
	{
		if (this.isDead)
		{
			return
		}

		this.sprite.setVelocityX(this.moveSpeed)

		if (this.sprite.x >= this.maxX)
		{
			this.stateMachine.setState('move-left')
		}
	}

	private moveLeftOnEnter()
	{
		if (this.isDead)
		{
			return
		}

		this.facing = -1
		this.sprite.play(this.animationKey('move-left'), true)
	}

	private moveLeftOnUpdate()
	{
		if (this.isDead)
		{
			return
		}

		this.sprite.setVelocityX(-this.moveSpeed)

		if (this.sprite.x <= this.minX)
		{
			this.stateMachine.setState('move-right')
		}
	}

	private deadOnEnter()
	{
		this.isDead = true
		this.sprite.setData('isDead', true)
		this.sprite.setDepth(this.sprite.depth - 1)
		this.sprite.setVelocityX(0)
		this.sprite.setIgnoreGravity(false)
		this.sprite.setSensor(true)
		const body = this.sprite.body as MatterJS.BodyType
		body.collisionFilter.mask = GROUND_COLLISION_CATEGORY

		if (this.facing > 0)
		{
			this.sprite.play(this.animationKey('death-right'), true)
			return
		}

		this.sprite.play(this.animationKey('death-left'), true)
	}

	private landAfterDeath(surfaceBody: MatterJS.BodyType)
	{
		if (this.landedAfterDeath)
		{
			return
		}

		this.landedAfterDeath = true
		this.sprite.setVelocity(0, 0)
		this.sprite.setIgnoreGravity(true)
		this.sprite.setStatic(true)
		this.sprite.anims.stop()

		const landingFrame = this.facing > 0
			? (2 * TPV_SPRITESHEET_COLUMNS) + 4
			: (3 * TPV_SPRITESHEET_COLUMNS) + 4
		this.sprite.setFrame(landingFrame)

		const hasBottomGroundOffset = (surfaceBody as MatterJS.BodyType & { hasBottomGroundOffset?: boolean }).hasBottomGroundOffset === true
		if (!hasBottomGroundOffset)
		{
			this.sprite.y += this.nonGroundLandingYOffset
		}

		this.scene.time.delayedCall(TPV_DISAPPEAR_DELAY_MS, () => {
			this.scene.tweens.add({
				targets: this.sprite,
				alpha: 0,
				duration: TPV_FADE_OUT_DURATION_MS,
				onComplete: () => {
					this.sprite.destroy()
				}
			})
		})
	}

	private handleStomped(target: Phaser.Physics.Matter.Sprite)
	{
		if (this.sprite !== target || this.isDead)
		{
			return
		}

		this.stateMachine.setState('dead')
	}
}